import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { map } from "rxjs/operators";
import * as moment from "moment";

@Injectable({
  providedIn: 'root'
})
export class EventService {
  
  userJoinedNewEvent = new EventEmitter<any>();
  userLeftEvent = new EventEmitter<any>();

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService
  ) {}

  getEventUsers(eventId: number) {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/events/get-users/${eventId}`
    );
  }

  getPost(postId: number, eventId: number) {
    return this.httpClient.get<any>(
      `${environment.serverPath}/posts/get/${postId}?eventId=${eventId}`
    );
  }

  getAllEvents() {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/events/get-events`
    );
  }

  createEvent(model: any) {
    return this.httpClient.post<any[]>(
      `${environment.serverPath}/events/create-event`,
      model
    );
  }

  editEvent(model: any, id: number) {
    return this.httpClient.put(
      `${environment.serverPath}/events/create-event/${id}`,
      model
    );
  }

  joinEvent(model: any, isInvited: boolean = false) {
    return this.httpClient
      .post<any[]>(
        `${environment.serverPath}/events/join-event`,
        {eventId: model, isInvited}
      )
      .pipe(
        map((x) => {
          this.userJoinedNewEvent.emit();
          return x;
        })
      );
  }

  getJoinedEvents(profileId: number) {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/events/get-joined-events/${profileId}`
    );
  }

  leaveEvent(model: any) {
    return this.httpClient
      .post<any[]>(
        `${environment.serverPath}/events/leave-event`,
        model
      )
      .pipe(
        map((x) => {
          this.userLeftEvent.emit();
          return x;
        })
      );
  }

  changeEventCover(path: string) {
    return this.httpClient.post(
      `${environment.serverPath}/event/update-event-cover`,
      { path }
    );
  }

  getMyWall(page: number = 0, limit: number, eventId: number) {
    return this.httpClient
      .get<{ count: number; items: any[] }>(
        `${environment.serverPath}/posts/event-wall?page=${page}&limit=${limit}&eventId=${eventId}`
      )
      .pipe(
        map((x) => {
          x.items.forEach((o) => {
            this.updateCoverImage(o);
            this.updateProfileImage(o);
            if (o.comments && o.comments.length > 0) {
              o.comments.forEach((z) => {
                this.updateCoverImage(z.profile);
                this.updateProfileImage(z.profile);
              });
            }
          });
          return x;
        })
      );
  }

  updateProfileImage(profile: any) {
    if (!profile.profileImage) {
      profile.profileImage = "/assets/images/profile.jpg";
    } else {
      if (profile.profileImage.indexOf("http") === -1) {
        profile.profileImage = environment.serverPath + profile.profileImage;
      }
    }
  }

  updateCoverImage(profile: any) {
    if (!profile.profileCover) {
      profile.profileCover = "/assets/images/genericCover.png";
    } else {
      if (profile.profileCover.indexOf("http") === -1) {
        profile.profileCover = environment.serverPath + profile.profileCover;
      }
    }
  }

  getPostedOn(datetime: string) {
    const momentTIme = moment(datetime);
    const diffInSeconds = moment().diff(momentTIme, "seconds");
    let str = "";

    if (diffInSeconds < 60) {
      str = this.translateService.instant("timelineHourBeforeSeconds", {
        time: diffInSeconds,
      });
    } else if (diffInSeconds >= 60 && diffInSeconds < 3600) {
      str = this.translateService.instant("timelineHourBeforeMinuts", {
        time: Math.floor(moment().diff(momentTIme, "minutes")),
      });
    } else if (diffInSeconds >= 3600 && diffInSeconds < 86400) {
      str = this.translateService.instant("timelineHourBeforeHours", {
        time: Math.floor(moment().diff(momentTIme, "hours")),
      });
    } else if (diffInSeconds >= 86400 && diffInSeconds < 86400 * 10) {
      str = this.translateService.instant("timelineHourBeforeDays", {
        time: Math.floor(moment().diff(momentTIme, "days")),
      });
    } else {
      str = momentTIme.format("DD/MM/YYYY");
    }
    return str;
  }

  getAllProjects(eventId: number) {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/events/projects/${eventId}`
    );
  }

  createProject(model: any, eventId: number) {
    return this.httpClient.post(
      `${environment.serverPath}/events/projects/${eventId}`,
      model
    );
  }

  getProject(projectId: number) {
    return this.httpClient.get<any>(
      `${environment.serverPath}/events/project/${projectId}`
    );
  }

  getProjectComments(projectId: number) {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/project-comments/${projectId}`
    );
  }

  getProjectComment(projectId: number, commentId: number) {
    return this.httpClient.get<any>(
      `${environment.serverPath}/project-comments/${projectId}/${commentId}`
    );
  }

  createProjectComment(model: any, projectId: number) {
    return this.httpClient.post(
      `${environment.serverPath}/project-comments/${projectId}`,
      model
    );
  }

  deleteComment(commentId: number) {
    return this.httpClient.delete(
      `${environment.serverPath}/project-comments/${commentId}`
    );
  }

  getProjectById(projectId: number) {
    return this.httpClient.get<any>(
      `${environment.serverPath}/events/projectById/?&projectId=${projectId}`
    );
  }

  editProject(model: any, projectId: number) {
    return this.httpClient.post(
      `${environment.serverPath}/events/edit-project/${projectId}`,
      model
    );
  }

  getInterests() {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/api/admin/skills/interests`
    );
  }

  getExpertise() {
    return this.httpClient.get<any[]>(
      `${environment.serverPath}/api/admin/skills/expertise`
    );
  }
  sendInvitationTo(model: any) {
    return this.httpClient.post(
      `${environment.serverPath}/events/send-invitation`,
      model
    );
  }
  // sendInvitationToAll(model: any) {
  //   return this.httpClient.post(
  //     `${environment.serverPath}/events/invite-all`,
  //     model
  //   );
  // }
  isInvited(eventId: number) {
    return this.httpClient.get(
      `${environment.serverPath}/general/isInvited/event/${eventId}`
    );
  }
 deleteEvent(eventId: number) {
    return this.httpClient.delete(
      `${environment.serverPath}/events/delete-event`,
      { params: { eventId: eventId.toString() } }
    );
  }
changeEventAdmin(model: any) {
    return this.httpClient.put(
      `${environment.serverPath}/events/change-admin/`,
      model
    );
  }


}
